import React, { Fragment } from 'react';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { AvTooltip } from '@fabric-ui/components';
import { Transition } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { abbreviateNumber, iconSize } from '@fabric-ui/utils';
import { Box, Skeleton, SxProps } from '@mui/material';
import { getAssetTypeIcon } from '@utils/utils';
import { EVariant, ISummarizedCollapse, ISummarizedCollapseItem } from './types';

const SummarizedCollapse: React.FC<ISummarizedCollapse> = ({ items }) =>
  items.map(itemProps => (
    <Fragment key={itemProps.title}>
      {itemProps.isLoading ? (
        <Skeleton variant="text" height={21} width="30%" />
      ) : (
        <ErrorBoundary fallbackComponent={<div>Error loading the widget</div>}>
          <SummarizedCollapseItem {...itemProps} />
        </ErrorBoundary>
      )}
    </Fragment>
  ));

const Pill = ({ sx = {} }: { sx: SxProps }) => (
  <Box
    component="span"
    sx={{
      width: '7px',
      height: '16px',
      ...sx,
    }}
  />
);

const SummarizedCollapseItem: React.FC<ISummarizedCollapseItem> = ({
  title,
  data: d,
  showPills = false,
  getPillsStyleFunc = () => ({}),
  getLabelFunc = key => key || '',
  onSuccess = d => d,
  showIcon = true,
  variant = 'default',
  icon: customIcon,
}) => {
  const data = onSuccess(d);
  const getContent = (key, data) => {
    const icon = !showPills && getAssetTypeIcon(key);
    return (
      <Box sx={{ ...flex.itemsCenter, gap: 0.5 }}>
        {showIcon && (icon || customIcon) && <div style={{ ...flex.itemsCenter, flexShrink: 0 }}>{customIcon ? '' : icon}</div>}
        {showPills && <Pill sx={{ ...getPillsStyleFunc(key, data), width: '7px', height: '16px', borderRadius: '6px' }} />}
        {variant !== EVariant.values && (
          <Box
            sx={{
              fontSize: 12,
              maxWidth: '100px',
              color: 'var(--color-content-base-secondary)',
            }}>
            <AvTooltip>{getLabelFunc(key)}</AvTooltip>
          </Box>
        )}
        <Box component="span" sx={{ whiteSpace: 'nowrap', fontWeight: 600, fontSize: 13 }}>
          {variant === EVariant.percentage ? `${((data?.[key] || 0) * 100).toFixed(1)}%` : abbreviateNumber(data?.[key])}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ ...flex.row, gap: 2, whiteSpace: 'nowrap' }}>
      <Box
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--color-content-base-secondary)',
          ...flex.center,
          gap: 0.5,
        }}>
        {customIcon}
        {title}
        {customIcon ? '' : ':'}
      </Box>
      <Box sx={{ ...flex.itemsCenter, gap: 2 }}>
        {Object.keys(data || {}).map((key, index, arr) => (
          <Box key={key} sx={{ ...flex.itemsCenter, gap: variant === EVariant.transition ? 2 : '5px' }}>
            {getContent(key, data)}
            {variant === EVariant.transition && index < arr.length - 1 && (
              <Transition style={{ ...iconSize(16), color: 'var(--color-content-base-tertiary)' }} />
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default SummarizedCollapse;
